/**
 * documentDeletion — a SEQUÊNCIA da exclusão de um documento, num lugar só.
 *
 * ─── POR QUE ISTO NÃO É UM WRAPPER À TOA ────────────────────────────────────
 *
 * A ordem certa tem três passos e cada um só pode acontecer num momento:
 *
 *   1. **CALCULAR** o que devolver ao estoque — obrigatoriamente ANTES do
 *      delete, porque `stock_movements.quote_id`/`.service_order_id` são ON
 *      DELETE SET NULL e o vínculo some junto com o documento.
 *   2. **APAGAR** o documento — atomicamente, sem apagar filhos na mão antes
 *      (ver `src/lib/quoteDeletion.ts`).
 *   3. **APLICAR** a devolução — obrigatoriamente DEPOIS, porque escrever no
 *      estoque de um documento que ainda pode não morrer infla a prateleira.
 *
 * Essa ordem estava copiada em cinco caminhos de exclusão (tela do orçamento,
 * exclusão em massa, tela da OS, e os dois executores da fila offline), e em
 * TODOS ela estava errada do mesmo jeito: devolvia antes, apagava depois. O
 * defeito só aparecia quando o delete falhava — e ele falhava de verdade, pelo
 * CHECK da despesa cobrável. Com a sequência copiada, consertar quatro e
 * esquecer o quinto seria o resultado mais provável.
 *
 * ─── O QUE PROPAGA E O QUE NÃO PROPAGA ──────────────────────────────────────
 *
 * Só o `remove()` propaga o erro: falhar em apagar é a única falha que muda o
 * que o usuário pediu. Não conseguir CALCULAR a devolução não pode barrar a
 * exclusão (deixaria o app preso numa ação destrutiva já confirmada), e não
 * conseguir APLICAR acontece com o documento já morto — nos dois casos o
 * chamador recebe a bandeira e avisa, em vez de mentir que devolveu.
 */
import type {
  DocumentReturnResult,
  PreparedDocumentReturn,
} from '@/lib/stockDocumentReturn';

export interface DocumentDeletionOutcome<T> {
  /** O que o `remove()` devolveu. */
  result: T;
  /** A devolução que de fato aconteceu (`null` = não houve nenhuma). */
  returned: DocumentReturnResult | null;
  /** O documento morreu, mas a devolução não pôde ser aplicada. */
  returnFailed: boolean;
  /** Nem foi possível saber o que devolver; a exclusão seguiu assim mesmo. */
  prepareFailed: boolean;
}

export interface DocumentDeletionParams<T> {
  /**
   * Calcula o que voltaria ao estoque. `null` quando não há nada a devolver
   * (gatilho desligado, usuário disse que não, documento sem baixa).
   */
  prepareReturn: () => Promise<PreparedDocumentReturn | null>;
  /** Apaga o documento. **Tem que lançar em erro** — é o portão do passo 3. */
  remove: () => Promise<T>;
}

export async function runDocumentDeletion<T>(
  params: DocumentDeletionParams<T>,
): Promise<DocumentDeletionOutcome<T>> {
  const { prepareReturn, remove } = params;

  // 1) Calcular — antes do delete, enquanto o vínculo ainda existe.
  let prepared: PreparedDocumentReturn | null = null;
  let prepareFailed = false;
  try {
    prepared = await prepareReturn();
  } catch {
    prepareFailed = true;
  }

  // 2) Apagar. Um erro aqui sobe SEM ter tocado no estoque — este é o ponto
  //    inteiro deste módulo.
  const result = await remove();

  // 3) Só agora devolver.
  let returned: DocumentReturnResult | null = null;
  let returnFailed = false;
  if (prepared && prepared.products > 0) {
    try {
      const applied = await prepared.apply();
      returned = applied.products > 0 ? applied : null;
    } catch {
      returnFailed = true;
    }
  }

  return { result, returned, returnFailed, prepareFailed };
}
